import React from 'react';
import { Loader } from 'lucide-react';
import { CATEGORY_OPTIONS } from '../constants/categories';

type Props = {
  initial?: any;
  onCancel?: () => void;
  onSubmit: (data: any) => Promise<void> | void;
};

const MONTHS = ['jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];

export default function RecurringForm({ initial, onCancel, onSubmit }: Props) {
  const [form, setForm] = React.useState(() => ({
    name: initial?.name || '',
    category: initial?.category || CATEGORY_OPTIONS[0],
    type: initial?.type || 'fixed', // 'fixed' | 'variable'
    amount: initial?.amount ?? 0,
    dueDay: initial?.dueDay ?? 10,
    startMonth: initial?.startMonth ?? new Date().getMonth() + 1,
    startYear: initial?.startYear ?? new Date().getFullYear(),
    active: initial?.active ?? true,
  }));
  const [isLoading, setIsLoading] = React.useState(false);

  const [displayAmount, setDisplayAmount] = React.useState(() =>
    form.amount ? new Intl.NumberFormat(navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(form.amount) : ''
  );

  const handleChange = (key: string, value: any) => setForm((s) => ({ ...s, [key]: value }));

  const formatCurrency = (value: number) => new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'BRL' }).format(value || 0);

  const handleMoneyChange = (raw: string) => {
    const digits = raw.replace(/[^\d]/g, '');
    if (!digits) {
      setDisplayAmount('');
      handleChange('amount', 0);
      return;
    }
    const num = parseFloat(digits) / 100;
    setDisplayAmount(new Intl.NumberFormat(navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num));
    handleChange('amount', num);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    try {
      const payload = {
        name: form.name,
        category: form.category,
        type: form.type,
        amount: Number(form.amount),
        dueDay: Number(form.dueDay),
        startMonth: Number(form.startMonth),
        startYear: Number(form.startYear),
        active: !!form.active,
      };
      await onSubmit(payload);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Nome</label>
        <input
          placeholder="Ex: Aluguel, Conta de Luz"
          value={form.name}
          onChange={(e) => handleChange('name', e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-gray-900"
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">Categoria</label>
          <select
            value={form.category}
            onChange={(e) => handleChange('category', e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 appearance-none text-gray-900 bg-white"
          >
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">Tipo</label>
          <select
            value={form.type}
            onChange={(e) => handleChange('type', e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 appearance-none text-gray-900 bg-white"
          >
            <option value="fixed">Fixa</option>
            <option value="variable">Variável</option>
          </select>
        </div>
      </div>

      {form.type === 'fixed' && (
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">Valor</label>
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-gray-500">{formatCurrency(0).replace(/[\d.,\s]/g, '')}</span>
            <input
              type="text"
              placeholder="0,00"
              value={displayAmount}
              onChange={(e) => handleMoneyChange(e.target.value)}
              className="w-full border border-gray-300 pl-10 pr-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-gray-900"
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">Dia do Vencimento</label>
          <input
            type="number"
            min={1}
            max={28}
            value={form.dueDay}
            onChange={(e) => handleChange('dueDay', Number(e.target.value))}
            className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-gray-900"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">Mês Início</label>
          <select
            value={form.startMonth}
            onChange={(e) => handleChange('startMonth', Number(e.target.value))}
            className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 appearance-none text-gray-900 bg-white"
          >
            {MONTHS.map((m, idx) => (
              <option key={m} value={idx + 1}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">Ano</label>
          <input
            type="number"
            value={form.startYear}
            onChange={(e) => handleChange('startYear', Number(e.target.value))}
            className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-gray-900"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.active} onChange={(e) => handleChange('active', e.target.checked)} />
          Ativa
        </label>
      </div>

      <div className="flex gap-3 pt-2">
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={isLoading} className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            Cancelar
          </button>
        )}
        <button type="submit" disabled={isLoading} className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-md font-medium hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          {isLoading && <Loader className="w-4 h-4 animate-spin" />}
          {isLoading ? 'Processando...' : (initial ? 'Atualizar' : 'Criar Conta')}
        </button>
      </div>
    </form>
  );
}
